/**
 * The service corridor — Act 1's first room made entirely for people.
 *
 * This is not a smaller hall. The halls used haze and width to make a player
 * feel that she might disappear into them; this L is narrow, clean and lit end
 * to end, so its three bays and one pipe lane are the complete list of places
 * where a pig can briefly be out of sight. Keeping the shell here, and deriving
 * both collision and stealth from it, is what makes that spatial claim survive
 * an edit to a wall or bay mouth.
 */

import * as THREE from 'three';
import { getAsset } from '@/models/registry';
import type { AssetDefinition } from '@/models/types';
import { getZone, type ZonePalette } from '@/render/palette';
import type { Fixture } from '@/render/lighting';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';

/** The zone this room is in. Everything else reads its palette from here. */
export const SERVICE_ZONE_ID = 'service';

/**
 * Every authored corridor dimension, in metres.
 *
 * The legs name their interior extents rather than reducing the L to a bounding
 * rectangle. That rectangle would make the southeast corner transparent — the
 * exact sight line the room exists to withhold until Rosalie reaches it.
 */
export const CORRIDOR = {
  floorY: 0,
  ceiling: 2.6,
  wallThickness: 0.2,
  longMinX: -1.2,
  longMaxX: 1.2,
  longMinZ: -9.0,
  longMaxZ: 10.2,
  shortMinX: -1.2,
  shortMaxX: 12.0,
  shortMinZ: 7.8,
  shortMaxZ: 10.2,
  bayDepth: 2.0,
  bayLength: 2.4,
  baySpacing: 5.5,
  pipeOuterFaceX: 1.0,
  /**
   * The same 0.2 m stand-off from the wall, on the short leg's south wall.
   *
   * Derived from `shortMinZ` by the same offset `pipeOuterFaceX` has from
   * `longMaxX`, so the run reads as one run turning a corner rather than as two
   * runs that happen to meet.
   */
  pipeOuterFaceZ: 8.0,
  pipeLowerY: 0.25,
  pipeUpperY: 0.45,
  /** Where the short leg's run stops: clear of the mat, so it is not a step. */
  pipeShortLegMaxX: 10.6,
} as const;

/** Where she comes in. Near end of the long leg, facing +Z. */
export const SERVICE_SPAWN = { x: 0, y: 0.25, z: -8.2, facing: 0 } as const;

/** The three bays, so the beat's flags and the stealth cover agree with the room. */
export const BAYS: readonly { id: string; minX: number; maxX: number; minZ: number; maxZ: number }[] = [
  { id: 'bay-junction', minX: -3.2, maxX: -1.2, minZ: -6.2, maxZ: -3.8 },
  { id: 'bay-bin', minX: 1.2, maxX: 3.2, minZ: -0.7, maxZ: 1.7 },
  { id: 'bay-washdown', minX: -3.2, maxX: -1.2, minZ: 4.8, maxZ: 7.2 },
];

/** The wet apron the hose makes. The director washes the trail inside this box. */
export const WASHDOWN_APRON = { minX: -3.0, maxX: -1.3, minZ: 5.0, maxZ: 7.0 };

/** Centre of the pressure mat, and where the personnel door stands. */
export const DOOR_AT = { x: 12.0, z: 9.0 };
/**
 * The dark, bounded threshold beyond the personnel door.
 *
 * Its inner edge is derived from the short leg's far edge rather than copied as
 * `12.0`: beat 4 finishes 0.3 m beyond that edge, and a moved door must carry
 * the floor that makes its flag reachable with it.
 */
export const VESTIBULE = { minX: CORRIDOR.shortMaxX, maxX: 14.8, minZ: 8.2, maxZ: 9.8 } as const;
export const MAT_AT = { x: 11.2, z: 9.0 };

const DOOR_WIDTH = 1.02;
const PIPE_MODULE_LENGTH = 2.4;
const PIPE_ASSET_HEIGHT = 0.47;

// Resolved in an IIFE for the reason `fattening.ts` gives: a module-level `if`
// guard reads as proof to a human and as nothing at all to the checker.
const zone: ZonePalette = (() => {
  const found = getZone(SERVICE_ZONE_ID);
  if (!found) throw new Error('service corridor: service zone missing from the palette');
  return found;
})();

// Albedo by index into the zone's own surface list, darkest to lightest. Fresh
// literals here would be this room quietly leaving its own palette.
function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`service corridor: surface ${index} missing from service palette`);
  return colour;
}

const CONCRETE_DARK = zoneSurface(0);
const CONCRETE_FLOOR = zoneSurface(1);
const CONCRETE_WALL = zoneSurface(2);
const STEEL = zoneSurface(4);

function spanCentre(min: number, max: number): number {
  return (min + max) / 2;
}

function spanHalf(min: number, max: number): number {
  return (max - min) / 2;
}

function shellBox(
  name: string,
  half: HallSolid['half'],
  pos: HallSolid['pos'],
  colour: number,
): HallSolid {
  return { name, half, pos, colour, kind: 'mineral' };
}

function assetDefinitionOrThrow(id: string): AssetDefinition {
  const asset = getAsset(id);
  if (!asset) throw new Error(`service corridor: asset "${id}" is not registered`);
  return asset;
}

function assetOrThrow(id: string): THREE.Group {
  return assetDefinitionOrThrow(id).create();
}

interface BayPropPlacement extends Placement {
  /** The authored asset and the footprint its collision box is derived from. */
  assetId: 'wheeled-bin' | 'junction-cabinet' | 'hose-reel' | 'feed-sacks';
  footprint: AssetDefinition['footprint'];
}

/**
 * Space left between a bay's back-wall face and a piece mounted against it.
 *
 * The first dressing pass put each rotated object on the wall centre line. Half
 * of its declared depth was therefore embedded in concrete: it looked like a
 * placement error and let a player judge the wall's collision as unreliable.
 * Keeping a real clearance after the rotated footprint stops that contact from
 * becoming z-fighting if an asset's honest footprint grows later.
 */
const PROP_WALL_CLEARANCE = 0.02;

/**
 * The four substantial bay props, once, for both their visible placements and
 * their invisible collision boxes.
 *
 * The bay-floor repair taught the expensive version of this rule: a room whose
 * shown object and physical object are derived from different data eventually
 * has one moved without the other. Their coordinates, yaw and declared
 * footprint live together here so every later move updates both halves.
 */
const BAY_PROP_PLACEMENTS: readonly BayPropPlacement[] = (() => {
  const junctionCabinet = assetDefinitionOrThrow('junction-cabinet');
  const feedSacks = assetDefinitionOrThrow('feed-sacks');
  const wheeledBin = assetDefinitionOrThrow('wheeled-bin');
  const hoseReel = assetDefinitionOrThrow('hose-reel');

  return [
    {
      assetId: 'junction-cabinet',
      footprint: junctionCabinet.footprint,
      // π/2 turns local depth into world X. The cabinet belongs east of the
      // west bay's inner wall face, not halfway through its concrete thickness.
      x: BAYS[0]!.minX + junctionCabinet.footprint.length / 2 + PROP_WALL_CLEARANCE,
      y: 0,
      z: -5.0,
      rotY: Math.PI / 2,
    },
    {
      assetId: 'feed-sacks',
      footprint: feedSacks.footprint,
      // These sit free of the back wall already. Moving them would turn a
      // collision repair into an unreviewed change to the wash-down staging.
      x: -2.35,
      y: 0,
      z: -5.0,
    },
    {
      assetId: 'wheeled-bin',
      footprint: wheeledBin.footprint,
      // The east bay opens westward, so its back-wall face is approached by
      // subtracting the rotated local depth rather than adding it.
      x: BAYS[1]!.maxX - wheeledBin.footprint.length / 2 - PROP_WALL_CLEARANCE,
      y: 0,
      z: 0.5,
      rotY: Math.PI / 2,
    },
    {
      assetId: 'hose-reel',
      footprint: hoseReel.footprint,
      // Like the cabinet, this is on a west bay wall. Its y is part of the
      // shared placement: resetting the collider to floor level leaves a
      // second, invisible obstacle below the wall-mounted reel.
      x: BAYS[2]!.minX + hoseReel.footprint.length / 2 + PROP_WALL_CLEARANCE,
      y: 1.1,
      z: 6.0,
      rotY: Math.PI / 2,
    },
  ];
})();

/** Collision-only boxes for the same placements `dressServiceCorridor()` draws. */
function bayPropSolids(): HallSolid[] {
  return BAY_PROP_PLACEMENTS.map((prop) => {
    const localHalf = {
      x: prop.footprint.width / 2,
      y: prop.footprint.height / 2,
      z: prop.footprint.length / 2,
    };
    // Rapier receives axis-aligned cuboids here. A quarter-turned asset has its
    // local width and depth exchanged in world space; preserving the authored
    // local axes would make the bin solid beside itself and walk-through inside.
    const quarterTurn = Math.abs(prop.rotY ?? 0) === Math.PI / 2;
    return {
      name: `prop-${prop.assetId}-solid`,
      half: {
        x: quarterTurn ? localHalf.z : localHalf.x,
        y: localHalf.y,
        z: quarterTurn ? localHalf.x : localHalf.z,
      },
      // ── `+ localHalf.y`, and it is the difference between a solid and a kerb ──
      //
      // A `Placement`'s `y` is where the asset's ORIGIN goes, and
      // `docs/asset-conventions.md` puts every asset's origin at its ground
      // contact point. A `HallSolid`'s `pos` is the box's CENTRE — see the
      // `pen-rail-solid` boxes in `farrowing.ts`, which compute theirs as the
      // midpoint of a top and a bottom for exactly this reason.
      //
      // Passing the placement's y straight through sinks every box half its own
      // height into the floor: the sacks would have stopped her at 195 mm and
      // the bin at 575 mm, so she would have walked up and over both of them.
      // That is a worse bug than the walk-through it was fixing, and neither
      // typecheck, lint nor a screenshot can see it.
      pos: { x: prop.x, y: (prop.y ?? 0) + localHalf.y, z: prop.z },
      colour: STEEL,
      kind: 'metal' as const,
      // The authored asset is the visible object. This box deliberately enters
      // the shared collision/occlusion list without being drawn over that mesh.
      visible: false,
    };
  });
}

/**
 * Every box in the corridor shell, mesh and collider alike. See `solids.ts`.
 *
 * Door leaves are collision-only boxes because the authored door assets contain
 * the legible frame and hardware. Drawing a plain box over those would waste a
 * call hiding the better mesh; omitting the box would turn a visibly shut door
 * into a phantom opening until the director is allowed to own its collider.
 */
export function serviceCorridorSolids(): HallSolid[] {
  const solids: HallSolid[] = [];
  const { ceiling, floorY, longMaxX, longMaxZ, longMinX, longMinZ, shortMaxX, shortMaxZ, shortMinZ, wallThickness } = CORRIDOR;
  const wallHalf = wallThickness / 2;
  const longCentreX = spanCentre(longMinX, longMaxX);
  const longCentreZ = spanCentre(longMinZ, longMaxZ);

  // The two floor and ceiling slabs meet on the elbow line rather than overlap.
  // Coplanar overlap is not harmless: it z-fights in motion and reads as a wet
  // decal, which would accidentally tell the player that water is already here.
  solids.push(
    shellBox(
      'floor-long',
      { x: spanHalf(longMinX, longMaxX), y: 0.1, z: spanHalf(longMinZ, longMaxZ) },
      { x: longCentreX, y: floorY - 0.1, z: longCentreZ },
      CONCRETE_FLOOR,
    ),
    shellBox(
      'floor-short',
      { x: spanHalf(longMaxX, shortMaxX), y: 0.1, z: spanHalf(shortMinZ, shortMaxZ) },
      { x: spanCentre(longMaxX, shortMaxX), y: floorY - 0.1, z: spanCentre(shortMinZ, shortMaxZ) },
      CONCRETE_FLOOR,
    ),
    shellBox(
      'ceiling-long',
      { x: spanHalf(longMinX - wallThickness, longMaxX + wallThickness), y: 0.1, z: spanHalf(longMinZ - wallThickness, longMaxZ + wallThickness) },
      { x: longCentreX, y: ceiling + 0.1, z: longCentreZ },
      CONCRETE_DARK,
    ),
    shellBox(
      'ceiling-short',
      { x: spanHalf(longMaxX, shortMaxX + wallThickness), y: 0.1, z: spanHalf(shortMinZ - wallThickness, shortMaxZ + wallThickness) },
      { x: spanCentre(longMaxX, shortMaxX + wallThickness), y: ceiling + 0.1, z: spanCentre(shortMinZ, shortMaxZ) },
      CONCRETE_DARK,
    ),
  );

  const addLongWall = (name: string, x: number, minZ: number, maxZ: number): void => {
    solids.push(shellBox(
      name,
      { x: wallHalf, y: ceiling / 2, z: spanHalf(minZ, maxZ) },
      { x, y: ceiling / 2, z: spanCentre(minZ, maxZ) },
      CONCRETE_WALL,
    ));
  };

  // Bay mouths are absences in the same list that makes the wall solid. Keeping
  // an opening as a dressing convention is how a moved cabinet becomes a wall
  // Rosalie can see through but not walk through.
  addLongWall('wall-west-near', longMinX - wallHalf, longMinZ, BAYS[0]!.minZ);
  addLongWall('wall-west-middle', longMinX - wallHalf, BAYS[0]!.maxZ, BAYS[2]!.minZ);
  addLongWall('wall-west-far', longMinX - wallHalf, BAYS[2]!.maxZ, longMaxZ);
  addLongWall('wall-east-near', longMaxX + wallHalf, longMinZ, BAYS[1]!.minZ);
  addLongWall('wall-east-middle', longMaxX + wallHalf, BAYS[1]!.maxZ, shortMinZ);

  // ── The bays are rooms, not gaps in a wall ──────────────────────────────────
  //
  // They were authored as absences in `addLongWall` above and nothing else, so
  // each bay mouth opened onto no floor at all: walking into one dropped her out
  // of the world. Reported from an actual play-through, and invisible to every
  // gate we have — the budget counts what exists, the walk test never turned off
  // the corridor's centre line, and a hole reads in a screenshot exactly like a
  // dark alcove.
  //
  // So a bay is five boxes and the shell builds them from `BAYS`, which is the
  // same list the stealth cover and the beat's flags are derived from. Moving a
  // bay therefore moves its floor; adding a fourth one cannot produce a fourth
  // void. The floor and ceiling keep the `floor-`/`ceiling` name prefixes that
  // `serviceCorridorStealth.ts` filters on — a bay floor that shadowed itself
  // would make the room's only cover read as lit.
  for (const bay of BAYS) {
    // Which side of the corridor it opens from decides where its back wall is.
    // West bays open toward +X, east bays toward −X, and the sign is the only
    // difference between them.
    const opensEast = bay.minX >= longMaxX;
    const backX = opensEast ? bay.maxX + wallHalf : bay.minX - wallHalf;
    // The shell spans from the back wall's outer face to the corridor wall's, so
    // the bay's corners meet the long wall's cut ends with no seam to fall through.
    const shellMinX = opensEast ? longMaxX : bay.minX - wallThickness;
    const shellMaxX = opensEast ? bay.maxX + wallThickness : longMinX;
    const shellCentreX = spanCentre(shellMinX, shellMaxX);
    const shellHalfX = spanHalf(shellMinX, shellMaxX);
    const bayCentreZ = spanCentre(bay.minZ, bay.maxZ);

    solids.push(
      shellBox(
        `floor-${bay.id}`,
        { x: shellHalfX, y: 0.1, z: spanHalf(bay.minZ, bay.maxZ) },
        { x: shellCentreX, y: floorY - 0.1, z: bayCentreZ },
        CONCRETE_FLOOR,
      ),
      shellBox(
        `ceiling-${bay.id}`,
        { x: shellHalfX, y: 0.1, z: spanHalf(bay.minZ - wallThickness, bay.maxZ + wallThickness) },
        { x: shellCentreX, y: ceiling + 0.1, z: bayCentreZ },
        CONCRETE_DARK,
      ),
      shellBox(
        `wall-${bay.id}-back`,
        { x: wallHalf, y: ceiling / 2, z: spanHalf(bay.minZ - wallThickness, bay.maxZ + wallThickness) },
        { x: backX, y: ceiling / 2, z: bayCentreZ },
        CONCRETE_WALL,
      ),
    );

    // The two cheeks. These are what make a bay dark: the corridor's strips are
    // in the ceiling of the long leg, and without cheeks their light reached
    // straight into the alcove that is supposed to be the room's only cover.
    for (const [suffix, z] of [['near', bay.minZ - wallHalf], ['far', bay.maxZ + wallHalf]] as const) {
      solids.push(shellBox(
        `wall-${bay.id}-${suffix}`,
        { x: shellHalfX, y: ceiling / 2, z: wallHalf },
        { x: shellCentreX, y: ceiling / 2, z },
        CONCRETE_WALL,
      ));
    }
  }

  // The near door is a real shut leaf with shoulders around it. It still remains
  // a separate collider so a later open pose has one obvious object to replace.
  const nearDoorMinX = -DOOR_WIDTH / 2;
  const nearDoorMaxX = DOOR_WIDTH / 2;
  solids.push(
    shellBox(
      'end-near-west',
      { x: spanHalf(longMinX - wallThickness, nearDoorMinX), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(longMinX - wallThickness, nearDoorMinX), y: ceiling / 2, z: longMinZ - wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'end-near-east',
      { x: spanHalf(nearDoorMaxX, longMaxX + wallThickness), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(nearDoorMaxX, longMaxX + wallThickness), y: ceiling / 2, z: longMinZ - wallHalf },
      CONCRETE_WALL,
    ),
    { name: 'service-door-leaf', half: { x: DOOR_WIDTH / 2, y: 1, z: 0.04 }, pos: { x: 0, y: 1, z: longMinZ }, colour: STEEL, kind: 'metal', visible: false },
  );

  // This is the unchamfered blind corner. The east wall stops before it and the
  // short leg's south wall begins there, so no diagonal or corner gap gives the
  // player a line of sight into the branch early.
  solids.push(
    shellBox(
      'wall-short-south',
      { x: spanHalf(longMaxX, shortMaxX), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(longMaxX, shortMaxX), y: ceiling / 2, z: shortMinZ - wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-short-north',
      { x: spanHalf(longMinX, shortMaxX), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(longMinX, shortMaxX), y: ceiling / 2, z: shortMaxZ + wallHalf },
      CONCRETE_WALL,
    ),
  );

  const farDoorMinZ = DOOR_AT.z - DOOR_WIDTH / 2;
  const farDoorMaxZ = DOOR_AT.z + DOOR_WIDTH / 2;
  solids.push(
    shellBox(
      'end-short-near',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(shortMinZ - wallThickness, farDoorMinZ) },
      { x: shortMaxX + wallHalf, y: ceiling / 2, z: spanCentre(shortMinZ - wallThickness, farDoorMinZ) },
      CONCRETE_WALL,
    ),
    shellBox(
      'end-short-far',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(farDoorMaxZ, shortMaxZ + wallThickness) },
      { x: shortMaxX + wallHalf, y: ceiling / 2, z: spanCentre(farDoorMaxZ, shortMaxZ + wallThickness) },
      CONCRETE_WALL,
    ),
    { name: 'personnel-door-leaf', half: { x: 0.04, y: 1.025, z: DOOR_WIDTH / 2 }, pos: { x: DOOR_AT.x, y: 1.025, z: DOOR_AT.z }, colour: STEEL, kind: 'metal', visible: false },
  );

  // The beat flag lies 0.3 m past the door, but the old short-leg floor ended
  // exactly at its plane. This is an actual playable void, not a cosmetic
  // threshold: once the door opened she had to fall out of the world to finish.
  // The floor begins exactly at `shortMaxX`, so the existing split door wall at
  // that seam remains supported instead of hanging over a new gap.
  solids.push(
    shellBox(
      'floor-vestibule',
      { x: spanHalf(VESTIBULE.minX, VESTIBULE.maxX), y: 0.1, z: spanHalf(VESTIBULE.minZ, VESTIBULE.maxZ) },
      { x: spanCentre(VESTIBULE.minX, VESTIBULE.maxX), y: floorY - 0.1, z: spanCentre(VESTIBULE.minZ, VESTIBULE.maxZ) },
      CONCRETE_FLOOR,
    ),
    shellBox(
      'ceiling-vestibule',
      { x: spanHalf(VESTIBULE.minX, VESTIBULE.maxX + wallThickness), y: 0.1, z: spanHalf(VESTIBULE.minZ - wallThickness, VESTIBULE.maxZ + wallThickness) },
      { x: spanCentre(VESTIBULE.minX, VESTIBULE.maxX + wallThickness), y: ceiling + 0.1, z: spanCentre(VESTIBULE.minZ, VESTIBULE.maxZ) },
      CONCRETE_DARK,
    ),
    shellBox(
      'wall-vestibule-north',
      { x: spanHalf(VESTIBULE.minX, VESTIBULE.maxX), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(VESTIBULE.minX, VESTIBULE.maxX), y: ceiling / 2, z: VESTIBULE.maxZ + wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-vestibule-south',
      { x: spanHalf(VESTIBULE.minX, VESTIBULE.maxX), y: ceiling / 2, z: wallHalf },
      { x: spanCentre(VESTIBULE.minX, VESTIBULE.maxX), y: ceiling / 2, z: VESTIBULE.minZ - wallHalf },
      CONCRETE_WALL,
    ),
    shellBox(
      'wall-vestibule-end',
      { x: wallHalf, y: ceiling / 2, z: spanHalf(VESTIBULE.minZ, VESTIBULE.maxZ) },
      { x: VESTIBULE.maxX + wallHalf, y: ceiling / 2, z: spanCentre(VESTIBULE.minZ, VESTIBULE.maxZ) },
      CONCRETE_WALL,
    ),
  );

  solids.push(...bayPropSolids());

  return solids;
}

/**
 * Five and only five real point lights.
 *
 * The fittings below outnumber them on purpose. D-027 found the hardware bound
 * by lights per fragment, not by the visible lamp meshes; a dark fitting costs a
 * draw call, while a sixth point light costs every lit fragment in the room.
 */
export function serviceCorridorFixtures(): Fixture[] {
  return [
    { kind: 'point', position: { x: 0, y: 2.25, z: -7.0 }, intensity: 6, range: 7 },
    { kind: 'point', position: { x: 0, y: 2.25, z: -1.0 }, intensity: 6, range: 7 },
    { kind: 'point', position: { x: 0, y: 2.25, z: 5.0 }, intensity: 6, range: 7 },
    { kind: 'point', position: { x: 3.5, y: 2.25, z: 9.0 }, intensity: 6, range: 7 },
    { kind: 'point', position: { x: 9.0, y: 2.25, z: 9.0 }, intensity: 6, range: 7 },
  ];
}

/** The fluorescent hum has a direction: it follows her toward the only way on. */
export function serviceCorridorAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [{ id: 'fluorescent-buzz', at: { x: 5.5, y: 2.25, z: 9.0 }, gain: 0.55 }];
}

/**
 * Centres of chainable 2.4 m pipe modules within a specified span.
 *
 * Neither authored pipe span is a multiple of the kit module. The final module
 * is therefore deliberately overlapped rather than allowed into a bay: an
 * overlap is visually quiet on an insulated run, whereas a pipe crossing the
 * mouth silently changes the room's usable cover.
 */
function moduleCentres(min: number, max: number): number[] {
  const centres: number[] = [];
  for (let centre = min + PIPE_MODULE_LENGTH / 2; centre + PIPE_MODULE_LENGTH / 2 < max; centre += PIPE_MODULE_LENGTH) {
    centres.push(centre);
  }
  centres.push(max - PIPE_MODULE_LENGTH / 2);
  return centres;
}

function pipePlacements(minZ: number, maxZ: number, y: number): Placement[] {
  return moduleCentres(minZ, maxZ).map((z) => ({ x: CORRIDOR.pipeOuterFaceX, y: y - PIPE_ASSET_HEIGHT, z }));
}

/**
 * The same run, turned the corner and carried along the short leg's south wall.
 *
 * This is not dressing. Beat 4's last stage asks her to be beside the personnel
 * door's frame when the handler stands on the mat, and without this the short leg
 * has **no cover of any kind** — the nearest is the long leg's pipe lane, eleven
 * metres from the door, which is not a window, it is a coin toss. Pipes running
 * to the door they serve is also the least invented thing that could be there.
 *
 * It costs no draw call: these enter the same `instanceGroup` as the long leg's
 * run, and `instanceGroup` merges per material (D-024). The corridor's draw-call
 * debt is real (see `render-budget.spec.ts`) and this does not add to it.
 */
function shortLegPipePlacements(minX: number, maxX: number, y: number): Placement[] {
  return moduleCentres(minX, maxX).map((x) => ({
    x,
    y: y - PIPE_ASSET_HEIGHT,
    z: CORRIDOR.pipeOuterFaceZ,
    rotY: Math.PI / 2,
  }));
}

/**
 * Dress the shell with the service kit.
 *
 * Repeated things all enter through `instanceGroup`, even where there is only
 * one current placement. That keeps a second cabinet or an extended pipe run a
 * content edit instead of a quiet return to per-mesh scene assembly.
 */
export function dressServiceCorridor(): THREE.Group {
  const dressing = new THREE.Group();
  dressing.name = 'service-corridor-dressing';

  // The strips are deliberately more numerous than the five lights. The ceiling
  // must read as one continuous lit utility run without spending a point light on
  // every fitting; narrow pale walls make that lie credible.
  dressing.add(instanceGroup(
    assetOrThrow('ceiling-tube'),
    [-7.5, -4.5, -1.5, 1.5, 4.5, 7.5].map((z) => ({ x: 0, y: CORRIDOR.ceiling, z })),
    'long-ceiling-tubes',
  ));
  dressing.add(instanceGroup(
    assetOrThrow('ceiling-tube'),
    [3.0, 6.0, 9.0].map((x) => ({ x, y: CORRIDOR.ceiling, z: 9.0, rotY: Math.PI / 2 })),
    'short-ceiling-tubes',
  ));

  const pipeSegments: Placement[] = [];
  for (const y of [CORRIDOR.pipeLowerY, CORRIDOR.pipeUpperY]) {
    pipeSegments.push(...pipePlacements(CORRIDOR.longMinZ, BAYS[1]!.minZ, y));
    pipeSegments.push(...pipePlacements(BAYS[1]!.maxZ, CORRIDOR.shortMinZ, y));
    pipeSegments.push(...shortLegPipePlacements(CORRIDOR.longMaxX, CORRIDOR.pipeShortLegMaxX, y));
  }
  dressing.add(instanceGroup(assetOrThrow('pipe-run'), pipeSegments, 'pipe-lane'));

  // These three bays are the whole cover list, so their dressing stays specific
  // and sparse. A fourth object that looked like a hiding place would change the
  // stealth lesson even if no system code knew it had happened.
  for (const prop of BAY_PROP_PLACEMENTS) {
    dressing.add(instanceGroup(assetOrThrow(prop.assetId), [prop], prop.assetId));
  }
  dressing.add(instanceGroup(
    assetOrThrow('hose-nozzle'),
    [{ x: -2.2, z: 5.55 }],
    'hose-nozzle',
  ));
  dressing.add(instanceGroup(
    assetOrThrow('floor-gully'),
    [{ x: -2.2, z: 6.0 }],
    'floor-gully',
  ));

  dressing.add(instanceGroup(
    assetOrThrow('hall-door'),
    [{ x: 0, z: CORRIDOR.longMinZ }],
    'service-door',
  ));
  dressing.add(instanceGroup(
    assetOrThrow('personnel-door'),
    [{ x: DOOR_AT.x, z: DOOR_AT.z, rotY: -Math.PI / 2 }],
    'personnel-door',
  ));
  dressing.add(instanceGroup(
    assetOrThrow('pressure-mat'),
    [{ x: MAT_AT.x, z: MAT_AT.z, rotY: Math.PI / 2 }],
    'pressure-mat',
  ));

  return dressing;
}

/** Build the corridor shell and dressing; director state deliberately lives elsewhere. */
export function createServiceCorridor(): THREE.Group {
  const room = new THREE.Group();
  room.name = 'service-corridor';
  room.add(solidMeshes(serviceCorridorSolids(), 'shell'));
  room.add(dressServiceCorridor());
  return room;
}
